/**
 * The panel's colours.
 *
 * These are Klooma's real brand colours, the same pair the web app draws with
 * (`klooma-frontend/src/assets/css/variable.css`): the red `#ed3661` and the
 * teal `#38b0bc`. The panel used to run on `#E4749D`, a washed-out pink that
 * belonged to no other Klooma surface, alongside a green "add" button and a
 * cyan "filter" button on the same row - three unrelated hues per screen.
 *
 * The rule the palette encodes: **one accent carries meaning.** Brand red is
 * the primary action and the current page. Teal is the secondary action.
 * Green, amber and red-orange are reserved for state (a chip, an alert), never
 * for a button, so a coloured button always means "this is the thing to press".
 */

/** Brand red, as three numbers, for the rules that need it inside an rgba(). */
export const BRAND_RGB = "237, 54, 97";

/** Brand teal, likewise. */
export const ACCENT_RGB = "56, 176, 188";

/**
 * The brand pair, and the only place the two colours meet - a 3px rule under a
 * page title. Copied from the web app so the two products draw the same line.
 */
export const BRAND_RULE = "linear-gradient(91.28deg, #df3636 2.74%, #38b0bc 106.85%)";

/** Ink on light, in four steps. All clear 4.5:1 on white. */
const ink = {
  strong: "#101828",
  base: "#344054",
  muted: "#667085",
  soft: "#98a2b3",
};

const neutral = {
  0: "#ffffff",
  25: "#fcfcfd",
  50: "#f9fafb",
  100: "#f2f4f7",
  200: "#e6e8ec",
  300: "#d0d5dd",
  400: "#98a2b3",
  500: "#667085",
  600: "#475467",
  700: "#344054",
  800: "#1d2939",
  900: "#101828",
};

const palette = {
  mode: "light" as const,

  primary: {
    lighter: "#fff1f4",
    light: "#f97392",
    main: "#ed3661",
    dark: "#c62348",
    darker: "#9b1836",
    contrastText: "#ffffff",

    // Legacy keys. The old theme invented `light1…dark2` and a handful of
    // screens still read them; they are kept so nothing renders transparent,
    // but they now resolve inside the new palette instead of beside it.
    light1: "#38b0bc",
    light2: "#fff1f4",
    light3: "#ffffff",
    dark1: "#101828",
    dark2: "#344054",
  },

  secondary: {
    lighter: "#eafafb",
    light: "#7ccfd8",
    main: "#38b0bc",
    dark: "#1a8f9b",
    contrastText: "#ffffff",
  },

  info: {
    lighter: "#eafafb",
    light: "#7ccfd8",
    main: "#38b0bc",
    dark: "#1a8f9b",
    contrastText: "#ffffff",
  },

  success: {
    lighter: "#e7f6ef",
    light: "#4cb98b",
    main: "#0f8a5f",
    dark: "#0a6b4a",
    contrastText: "#ffffff",
  },

  warning: {
    lighter: "#fef4e6",
    light: "#f0a955",
    main: "#c77400",
    dark: "#9a5a00",
    contrastText: "#ffffff",
  },

  error: {
    lighter: "#fdeceb",
    light: "#f07a72",
    main: "#d92d20",
    dark: "#ab2018",
    contrastText: "#ffffff",
  },

  grey: neutral,

  text: {
    primary: ink.strong,
    secondary: ink.muted,
    disabled: ink.soft,
  },

  divider: neutral[200],

  background: {
    // The page is white and stays white. The panel used to paint the content
    // area `rgb(238,242,246)` and float a white card on it, which read as a
    // screen inside a screen; content now sits on the page itself.
    default: "#ffffff",
    paper: "#ffffff",
    // The two chrome surfaces that are allowed to differ from the page: the
    // sidebar, and a table's head.
    sidebar: "#fbfbfc",
    subtle: neutral[50],
  },

  action: {
    hover: "rgba(16, 24, 40, 0.04)",
    selected: `rgba(${BRAND_RGB}, 0.08)`,
    disabled: neutral[400],
    disabledBackground: neutral[100],
    focus: `rgba(${BRAND_RGB}, 0.12)`,
  },
};

export default palette;
